/**
 * GTM Script Loader
 *
 * Cloudflare Tag Gateway auto-injects the GTM container script at the edge
 * (before our HTML reaches the browser), so we do NOT manually inject gtm.js.
 *
 * This module initializes the dataLayer for GTM (gtm.start + gtm.js event)
 * and verifies the container script was loaded by Cloudflare.
 */

import config from '../../common/config';
import { log, warn } from './logger';

let gtmLoaded = false;

/**
 * Initialize GTM dataLayer and verify Cloudflare Tag Gateway loaded the container.
 *
 * Pushes the gtm.start timestamp and gtm.js event that GTM needs to initialize,
 * then confirms the Cloudflare-injected script tag exists. No manual script
 * injection is performed — Cloudflare handles that at the edge.
 */
export function loadGTMContainer(): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (gtmLoaded) {
    return;
  }

  // Initialize dataLayer for GTM — required even though Cloudflare injects the script.
  // Without this push, GTM won't process dataLayer events.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  } as any);

  // Cloudflare Tag Gateway handles GTM script injection at the edge.
  // Detect the auto-injected script to confirm it's present.
  const cfGtmScript = document.querySelector(
    `script[src*="gtm.js"][src*="${config.gtm.platformWebContainerId}"]`
  ) || document.querySelector(
    `script[src^="${config.gtm.tagGatewayPath}/"]`
  );

  if (cfGtmScript) {
    gtmLoaded = true;
    log('[GTM] Container initialized, script loaded by Cloudflare Tag Gateway');
  }
  else {
    // Still mark as loaded — the CF script may not be in the DOM yet (async).
    // The gtm.start push ensures GTM initializes when the script does load.
    gtmLoaded = true;
    warn('[GTM] Cloudflare Tag Gateway script not yet detected — gtm.start pushed, waiting for async load');
  }
}

/**
 * Check if GTM is loaded
 */
export function isGTMLoaded(): boolean {
  return gtmLoaded;
}
